// Real-time price and date updates on the main auction listing page (cards with price and date).
// When an offer is made, the card updates instantly via ActionCable (no batch delay).
import consumer from "./consumer"

var MAX_IDS_PER_REFRESH = 60
var LISTING_REFRESH_DELAY_MS = 1500

function isAuctionListingRealtimePage() {
  if (!document.body) return false
  if (document.body.getAttribute("data-realtime-listing-prices") === "false") return false
  return !!document.getElementById("pills-online-auction")
}

function teardownAuctionListingRealtime() {
  if (window._auctionListingSubscription) {
    try { window._auctionListingSubscription.unsubscribe() } catch (e) {}
    window._auctionListingSubscription = null
  }
  if (window._auctionListingPollTimer) {
    try { clearTimeout(window._auctionListingPollTimer) } catch (e) {}
    window._auctionListingPollTimer = null
  }
  window._auctionListingPollStarted = false
}

// Parse displayed/formatted price (e.g. "230,00 €" or "2 500,00") to number for comparison.
function parsePriceToNumber(str) {
  if (str == null || typeof str !== "string") return NaN
  var cleaned = str.replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")
  return parseFloat(cleaned) || NaN
}

function listingEndedOverlayLabel() {
  var pane = document.getElementById("pills-online-auction")
  var fromDom = pane && pane.getAttribute("data-listing-ended-label")
  return (fromDom && fromDom.trim()) || "PÄÄTTYNYT"
}

// Per-card label from the server (e.g. ENDED + OFFER ACCEPTED). Real-time/poll code must not
// replace it with the generic "ENDED" string used for active listings when the clock runs out.
function listingEndedOverlayLabelForCard(cardCol) {
  if (!cardCol) return listingEndedOverlayLabel()
  var attr = cardCol.getAttribute("data-listing-status-label")
  if (attr != null && String(attr).trim() !== "") return String(attr).trim()
  return listingEndedOverlayLabel()
}

function activeSearchInputValue() {
  var el = document.getElementById("active_search")
  if (!el) return ""
  return String(el.value == null ? "" : el.value).trim().toLowerCase()
}

function hasListingActiveSearchFilter() {
  return !!document.getElementById("active_search")
}

// True when the sidebar filter is "Päättyneet" (session active_search === "false").
function listingEndedFilterSelected() {
  if (!hasListingActiveSearchFilter()) return false
  return activeSearchInputValue() === "false"
}

// When there is no #active_search (unexpected fragment), keep legacy behaviour: overlay ended
// cards instead of removing them, and skip purge (cannot know user intent).
function useEndedOverlayInsteadOfRemovingCard() {
  return !hasListingActiveSearchFilter() || listingEndedFilterSelected()
}

// Legacy bug: ended overlays on "Aktiiviset" left cards with .auction-listing-card-ended; those
// nodes were then skipped by polling (see :not(.auction-listing-card-ended)) and never healed.
// Remove any such online-auction cards whenever we are not in ended-list mode.
function purgeStaleEndedOverlaysOnActiveListing() {
  if (!hasListingActiveSearchFilter()) return
  if (listingEndedFilterSelected()) return
  var container = document.getElementById("pills-online-auction")
  if (!container) return
  // DOM cleanup only: these rows were already ended (overlay). Server tab totals count matching
  // auctions, not visible cards — do not adjust counts here (old DOM recount skipped them too).
  container.querySelectorAll(".auction-listing-card-ended[data-auction-id]").forEach(function (card) {
    card.remove()
  })
}

// On "Päättynyt" (ended) listing, every card is marked .auction-listing-card-ended for overlay
// behaviour, but those rows must still count toward tab totals and Yhteensä.
// Default is legacy behaviour: treat as active listing (exclude ended-marked cards from the
// online-auction count). Only the explicit ended mode from #active_search must flip that.
function excludeEndedMarkedOnlineCardsFromTabCount() {
  if (!hasListingActiveSearchFilter()) return true
  return !listingEndedFilterSelected()
}

// Count listing cards per tab. Only online listings have data-auction-id; announcements and
// sales use the same grid markup. "Lataa lisää" appends extra `.row` siblings, so sum every
// direct `.row > .col.d-flex` (skips nested .row inside card bodies).
function countTopLevelListingCards(container, excludeEnded) {
  if (!container) return 0
  var n = 0
  for (var j = 0; j < container.children.length; j++) {
    var row = container.children[j]
    if (!row || !row.classList || !row.classList.contains("row")) continue
    for (var i = 0; i < row.children.length; i++) {
      var c = row.children[i]
      if (!c || !c.classList || !c.classList.contains("col") || !c.classList.contains("d-flex")) continue
      if (excludeEnded && c.classList.contains("auction-listing-card-ended")) continue
      n++
    }
  }
  return n
}

function markAuctionListingCardEnded(cardCol) {
  if (!cardCol || cardCol.classList.contains("auction-listing-card-ended")) return false
  cardCol.classList.add("auction-listing-card-ended")
  cardCol.removeAttribute("data-ends-at")

  var wrap = cardCol.querySelector(".item-image")
  var label = listingEndedOverlayLabelForCard(cardCol)
  if (wrap) {
    var banner = wrap.querySelector(".item-text")
    if (banner) {
      banner.textContent = label
    } else {
      var el = document.createElement("div")
      el.className = "item-text"
      el.textContent = label
      wrap.appendChild(el)
    }
  }

  // Do not intercept link clicks: on "Päättyneet" the grid is meant to open auction details;
  // on "Aktiiviset" ended rows are removed from the DOM instead of overlay-blocked.
  return true
}

// Tab counts and "Yhteensä" come from the server (full result set, all pages). The grid only
// shows the current page plus optional "Lataa lisää" — never replace those totals by counting
// DOM nodes (realtime removes would turn e.g. (18) into (6)). Adjust only by explicit removals.
function adjustListingCountsAfterOnlineCardsRemoved(removedCount) {
  if (!removedCount || removedCount < 1) return
  var onlineTab = document.querySelector("#pills-online-auction-tab .tab-count")
  var m = onlineTab ? String(onlineTab.textContent || "").match(/\((\d+)\)/) : null
  // Require the expected "(n)" shape so we never change Yhteensä without the matching tab update.
  if (!m) return
  var nextOnline = Math.max(0, parseInt(m[1], 10) - removedCount)
  onlineTab.textContent = "(" + nextOnline + ")"
  var totalEl = document.querySelector(".item-total span.fw-sbold")
  if (!totalEl) return
  var raw = String(totalEl.textContent || "").replace(/\s/g, "")
  var totalN = parseInt(raw, 10)
  if (!isNaN(totalN)) totalEl.textContent = String(Math.max(0, totalN - removedCount))
}

function removeAuctionCard(auctionId) {
  const idStr = String(auctionId)
  const container = document.getElementById("pills-online-auction")
  let cards = container ? container.querySelectorAll("[data-auction-id=\"" + idStr + "\"]") : []
  if (!cards.length) cards = document.querySelectorAll("[data-auction-id=\"" + idStr + "\"]")
  var removedFromDom = 0
  var overlayMode = useEndedOverlayInsteadOfRemovingCard()
  cards.forEach(function (card) {
    if (overlayMode) {
      markAuctionListingCardEnded(card)
    } else {
      // Aktiiviset: ended auctions must leave the grid (server already excludes them on reload).
      card.remove()
      removedFromDom++
    }
  })
  if (removedFromDom) adjustListingCountsAfterOnlineCardsRemoved(removedFromDom)
}

function removeExpiredCards() {
  purgeStaleEndedOverlaysOnActiveListing()
  var container = document.getElementById("pills-online-auction")
  if (!container) return
  var now = Math.floor(Date.now() / 1000)
  var removedFromDom = 0
  var overlayMode = useEndedOverlayInsteadOfRemovingCard()
  container.querySelectorAll("[data-ends-at]:not(.auction-listing-card-ended)").forEach(function (card) {
    var endsAt = parseInt(card.getAttribute("data-ends-at"), 10)
    if (endsAt > 0 && endsAt < now) {
      if (overlayMode) {
        markAuctionListingCardEnded(card)
      } else {
        card.remove()
        removedFromDom++
      }
    }
  })
  if (removedFromDom) adjustListingCountsAfterOnlineCardsRemoved(removedFromDom)
}

function startEndedCardRemover() {
  if (window._endedCardRemoverStarted) return
  window._endedCardRemoverStarted = true
  removeExpiredCards()
  // 30s catches end times sooner after a long background tab; purge inside tick fixes stuck overlays.
  setInterval(removeExpiredCards, 30000)
}

function applyListingUpdate(auctionId, data) {
  if (data.ended) {
    removeAuctionCard(auctionId)
    return
  }
  if (data.highest_ad_bid == null && data.ad_publication_date == null) return
  const idStr = String(auctionId)
  const container = document.getElementById("pills-online-auction")
  let cards = container ? container.querySelectorAll("[data-auction-id=\"" + idStr + "\"]") : []
  if (!cards.length) cards = document.querySelectorAll("[data-auction-id=\"" + idStr + "\"]")
  cards.forEach(function (card) {
    if (card.classList.contains("auction-listing-card-ended")) return
    if (data.highest_ad_bid != null && data.highest_ad_bid !== "") {
      const priceEl = card.querySelector(".item-price")
      if (priceEl) {
        var newPriceNum = parsePriceToNumber(data.highest_ad_bid)
        var currentPriceNum = parsePriceToNumber(priceEl.textContent)
        // Only update if new price is >= current (avoids stale listing_prices cache overwriting real-time value)
        if (isNaN(currentPriceNum) || newPriceNum >= currentPriceNum) {
          priceEl.textContent = data.highest_ad_bid + " €"
        }
      }
    }
    if (data.ad_publication_date != null && data.ad_publication_date !== "") {
      const timeEl = card.querySelector(".item-time-value")
      if (timeEl) {
        timeEl.textContent = data.ad_publication_date
      } else {
        const itemTime = card.querySelector(".item-time")
        if (itemTime) {
          const textNode = Array.from(itemTime.childNodes).find(function (n) {
            return n.nodeType === Node.TEXT_NODE && n.textContent.trim()
          })
          if (textNode) textNode.textContent = " " + data.ad_publication_date
          else itemTime.appendChild(document.createTextNode(" " + data.ad_publication_date))
        }
      }
    }
    if (data.ends_at != null) {
      card.setAttribute("data-ends-at", data.ends_at)
    }
  })
}

function subscribeToAuctionListing() {
  var _wasDisconnected = false
  var sub = consumer.subscriptions.create("AuctionListingChannel", {
    connected() {
      if (_wasDisconnected) {
        // Reconnected after a drop — fetch current prices to catch up on missed broadcasts.
        _wasDisconnected = false
        refreshListingPricesFromServer()
      }
    },
    disconnected() {
      _wasDisconnected = true
    },
    received(data) {
      try {
        if (data.auction_id == null) return
        if (!(data.ended || data.highest_ad_bid != null || data.ad_publication_date != null)) return
        applyListingUpdate(data.auction_id, data)
        scheduleDelayedListingRefresh()
      } catch (e) {
        console.warn("[AuctionListingChannel] update error:", e)
      }
    }
  })
  return sub
}

var _listingRefreshTimeout = null

function refreshListingPricesFromServer() {
  purgeStaleEndedOverlaysOnActiveListing()
  const container = document.getElementById("pills-online-auction")
  if (!container) return
  const cards = container.querySelectorAll("[data-auction-id]:not(.auction-listing-card-ended)")
  const ids = []
  for (var i = 0; i < cards.length && ids.length < MAX_IDS_PER_REFRESH; i++) {
    const id = cards[i].getAttribute("data-auction-id")
    if (id && ids.indexOf(id) === -1) ids.push(id)
  }
  if (ids.length === 0) return
  const url = "/listing_prices?ids=" + encodeURIComponent(ids.join(","))
  fetch(url, { credentials: "same-origin" })
    .then(function (res) { return res.ok ? res.json() : null })
    .then(function (payload) {
      if (!payload || typeof payload !== "object") return
      Object.keys(payload).forEach(function (id) {
        const entry = payload[id]
        if (!entry) return
        if (entry.ended) {
          applyListingUpdate(id, { ended: true })
        } else if (entry.highest_ad_bid != null || entry.ad_publication_date != null) {
          applyListingUpdate(id, entry)
        }
      })
    })
    .catch(function () {})
}

function scheduleDelayedListingRefresh() {
  if (_listingRefreshTimeout) clearTimeout(_listingRefreshTimeout)
  _listingRefreshTimeout = setTimeout(function () {
    _listingRefreshTimeout = null
    refreshListingPricesFromServer()
  }, LISTING_REFRESH_DELAY_MS)
}

function setupVisibilityRefresh() {
  if (window._auctionListingVisibilitySetup) return
  window._auctionListingVisibilitySetup = true
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState !== "visible") return
    if (!isAuctionListingRealtimePage()) return
    purgeStaleEndedOverlaysOnActiveListing()
    // Re-create subscription if it was lost while tab was hidden.
    if (!window._auctionListingSubscription) {
      window._auctionListingSubscription = subscribeToAuctionListing()
    }
    refreshListingPricesFromServer()
  })

  // Handle Firefox bfcache restores (persisted=true pageshow). Firefox freezes page state
  // including prices when navigating back; visibilitychange may not fire in that case.
  // Refreshing here ensures prices are current when the user returns via browser back button.
  window.addEventListener("pageshow", function (ev) {
    if (!ev.persisted) return
    if (!isAuctionListingRealtimePage()) return
    purgeStaleEndedOverlaysOnActiveListing()
    refreshListingPricesFromServer()
  })

  // Handle internet reconnection (e.g. mobile network switching, brief disconnects).
  // Debounced to avoid multiple resubscription cycles when online fires rapidly.
  var _listingOnlineDebounce = null
  window.addEventListener("online", function () {
    if (document.body.getAttribute("data-realtime-listing-prices") === "false") return
    if (_listingOnlineDebounce) clearTimeout(_listingOnlineDebounce)
    _listingOnlineDebounce = setTimeout(function () {
      _listingOnlineDebounce = null
      if (!isAuctionListingRealtimePage()) return
      purgeStaleEndedOverlaysOnActiveListing()
      // Re-create subscription so ActionCable reconnects cleanly.
      if (window._auctionListingSubscription) {
        try { window._auctionListingSubscription.unsubscribe() } catch (e) {}
        window._auctionListingSubscription = null
      }
      window._auctionListingSubscription = subscribeToAuctionListing()
      refreshListingPricesFromServer()
    }, 300)
  })
}

function getListingPollIntervalMs() {
  const container = document.getElementById("pills-online-auction")
  if (!container) return 30000
  const count = countTopLevelListingCards(container, excludeEndedMarkedOnlineCardsFromTabCount())
  if (count > 300) return 0
  if (count > 200) return 60000
  if (count > 50) return 30000
  return 10000
}

function startListingPollIfNeeded() {
  if (window._auctionListingPollStarted) return
  if (document.body.getAttribute("data-realtime-listing-prices") === "false") return
  window._auctionListingPollStarted = true
  function tick() {
    if (document.visibilityState !== "visible") return
    if (!document.getElementById("pills-online-auction")) return
    var next = getListingPollIntervalMs()
    if (next > 0) {
      refreshListingPricesFromServer()
      window._auctionListingPollTimer = setTimeout(tick, next)
    } else {
      window._auctionListingPollTimer = setTimeout(tick, 60000)
    }
  }
  window._auctionListingPollTimer = setTimeout(tick, getListingPollIntervalMs())
}

function initAuctionListingRealtime() {
  if (!isAuctionListingRealtimePage()) {
    teardownAuctionListingRealtime()
    return
  }
  if (window._auctionListingSubscription) return
  window._auctionListingSubscription = subscribeToAuctionListing()
}

var _lastInitRefresh = 0

function scheduleListingInit() {
  setupVisibilityRefresh()

  if (!isAuctionListingRealtimePage()) {
    teardownAuctionListingRealtime()
    return
  }

  initAuctionListingRealtime()
  startListingPollIfNeeded()
  startEndedCardRemover()
  // Refresh prices immediately on every navigation to the listing page so bids placed
  // while the user was on the ad detail page are reflected without waiting for the poll.
  // Debounced 500ms to absorb DOMContentLoaded + turbolinks:load firing close together.
  var now = Date.now()
  if (document.getElementById("pills-online-auction") && now - _lastInitRefresh > 500) {
    _lastInitRefresh = now
    refreshListingPricesFromServer()
  }
  if (!window._auctionListingSubscription) {
    setTimeout(initAuctionListingRealtime, 400)
  }
  if (!window._auctionListingSubscription) {
    setTimeout(initAuctionListingRealtime, 800)
  }
}

document.addEventListener("monet:online-listings-replaced", function () {
  purgeStaleEndedOverlaysOnActiveListing()
  removeExpiredCards()
})

document.addEventListener("DOMContentLoaded", scheduleListingInit)
if (typeof Turbolinks !== "undefined" && Turbolinks.Events) {
  document.addEventListener("turbolinks:load", scheduleListingInit)
}
if (document.readyState !== "loading") {
  setTimeout(scheduleListingInit, 0)
}
document.addEventListener("DOMContentLoaded", function () {
  const tab = document.getElementById("pills-online-auction-tab")
  if (tab) {
    tab.addEventListener("shown.bs.tab", function () {
      initAuctionListingRealtime()
    })
  }
})

export { subscribeToAuctionListing, initAuctionListingRealtime }
